// The top control panel for /piano-drills: key, chord progression builder
// (chips, presets, and a typed-progression input), rhythm, inversion mode,
// keyboard range, tempo, mode + transport, and a MIDI status badge.
// Fully controlled by the parent — no internal state beyond trivial UI bits
// (there is none here; every value round-trips through props).
//
// `pocketMode` slims the panel down for the untimed "Stay in the pocket"
// surface: only Key, Range, Chords (with presets + typed input), and the
// MIDI badge render — no rhythm/voicing/tempo/transport.

import React from "react";
import { Play, Square } from "lucide-react";
import {
  KEYS,
  DIATONIC_CHORDS,
  chordLabel,
  RHYTHMS,
  VOICINGS,
  BASS_PATTERNS,
  INVERSION_MODES,
  PROGRESSION_PRESETS,
  MAX_BARS,
  MAX_PROGRESSION_CHORDS,
  rangeNoteOptions,
  keyByName,
} from "../../lib/pianoDrills";
import { PATTERN_PRESETS } from "../../lib/chordPattern";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { cn } from "../../lib/utils";

const MODES = ["guide", "score"];
const MODE_BLURB = {
  guide: "Guide: I play and highlight the keys.",
  score: "Score: you play, I judge accuracy and timing.",
};

// One-line hint under the Inversions chip group, per mode.
const INVERSION_HINTS = {
  roots: "Every chord in root position — the classic drill.",
  assigned: "Each bar calls an inversion — watch the slash chords.",
  nearest:
    "Each chord voiced closest to the last — add a bass pattern for the full comping feel.",
};

// Span like "2 octaves", rounded to one decimal.
const spanLabel = (low, high) => {
  const octaves = Math.round(((high - low) / 12) * 10) / 10;
  return `${octaves} octave${octaves === 1 ? "" : "s"}`;
};

export default function DrillControls({
  keyName,
  onKeyChange,
  progression,
  onProgressionChange,
  rhythmId,
  onRhythmChange,
  patternText,
  onPatternTextChange,
  patternError,
  voicingId,
  onVoicingChange,
  inversionModeId,
  onInversionModeChange,
  progressionText,
  onProgressionTextChange,
  progressionError,
  bassId,
  onBassChange,
  pocketMode = false,
  low,
  high,
  onRangeChange,
  bpm,
  onBpmChange,
  mode,
  onModeChange,
  metronome,
  onMetronomeChange,
  playing,
  onTogglePlay,
  midiStatus,
  midiInputs,
  drillError,
}) {
  const useFlats = (keyByName(keyName) || {}).useFlats || false;
  const rangeOptions = rangeNoteOptions(useFlats);
  const atMax = progression.length >= MAX_BARS;
  const midiReady = midiStatus === "ready" && !!midiInputs && midiInputs.length > 0;

  const setText = (text) => {
    if (onProgressionTextChange) onProgressionTextChange(text);
  };
  const typedActive = Boolean((progressionText || "").trim());

  // Any chip-builder interaction hands control back to the chips by clearing
  // the typed progression (non-empty text otherwise wins — see the parent).
  const addChord = (degree) => {
    if (atMax) return;
    setText("");
    onProgressionChange([...progression, degree]);
  };

  const removeAt = (index) => {
    const next = progression.slice();
    next.splice(index, 1);
    setText("");
    onProgressionChange(next);
  };

  return (
    <Card className="bg-card/90">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Drill setup</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Key */}
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Key
          </label>
          <Select value={keyName} onValueChange={onKeyChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KEYS.map((k) => (
                <SelectItem key={k.name} value={k.name}>
                  {k.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Rhythm */}
        {!pocketMode && (
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Rhythm
          </label>
          <div className="flex flex-wrap gap-1.5">
            {RHYTHMS.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onRhythmChange(r.id)}
                aria-pressed={rhythmId === r.id}
                title={r.desc}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-left text-sm transition-colors",
                  rhythmId === r.id
                    ? "border-primary bg-primary/10 font-medium"
                    : "border-border bg-background hover:border-primary/60 hover:bg-accent"
                )}
              >
                <div>{r.label}</div>
                <div className="text-xs font-normal text-muted-foreground">{r.desc}</div>
              </button>
            ))}
          </div>
          {rhythmId === "custom" && (
            <div className="mt-2 space-y-1.5">
              <input
                type="text"
                value={patternText}
                onChange={(e) => onPatternTextChange(e.target.value)}
                placeholder="[35] 1"
                aria-label="Custom pattern"
                spellCheck={false}
                className="w-full rounded-lg border border-border bg-background px-3 py-1.5 font-mono text-sm focus:border-primary focus:outline-none"
              />
              {patternError ? (
                <p className="text-xs text-amber-700">{patternError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Numbers are degrees of the current chord (1 root · 3 third · 5 fifth),
                  jianpu style: <code>[35]</code> together, <code>_</code> eighth,{" "}
                  <code>=</code> 16th, <code>.</code> dotted, <code>-</code> hold,{" "}
                  <code>0</code> rest, <code>'</code>/<code>,</code> octave up/down.
                  Short patterns repeat to fill the bar.
                </p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {PATTERN_PRESETS.map((p) => (
                  <button
                    key={p.text}
                    type="button"
                    title={p.desc}
                    onClick={() => onPatternTextChange(p.text)}
                    className="rounded-full border border-border bg-secondary px-2.5 py-1 font-mono text-xs hover:border-primary/60"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Bass (left hand) */}
        {!pocketMode && (
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Bass (left hand)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {BASS_PATTERNS.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => onBassChange(b.id)}
                aria-pressed={bassId === b.id}
                title={b.desc}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-sm transition-colors",
                  bassId === b.id
                    ? "border-primary bg-primary/10 font-medium"
                    : "border-border bg-background hover:border-primary/60 hover:bg-accent"
                )}
              >
                {b.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {(BASS_PATTERNS.find((b) => b.id === bassId) || BASS_PATTERNS[0]).desc}
          </p>
        </div>
        )}

        {/* Voicing — in nearest-voicing mode it only shapes bar 1 (later bars
            chain from it), so the chips de-emphasize but stay clickable. */}
        {!pocketMode && (
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Voicing
          </label>
          <div
            className={cn(
              "flex flex-wrap gap-1.5",
              inversionModeId === "nearest" && "opacity-70"
            )}
          >
            {VOICINGS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => onVoicingChange(v.id)}
                aria-pressed={voicingId === v.id}
                title={v.desc}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-sm transition-colors",
                  voicingId === v.id
                    ? "border-primary bg-primary/10 font-medium"
                    : "border-border bg-background hover:border-primary/60 hover:bg-accent"
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {(VOICINGS.find((v) => v.id === voicingId) || VOICINGS[0]).desc}
          </p>
          {inversionModeId === "nearest" && (
            <p className="mt-1 text-xs text-amber-700/90">
              In nearest-voicing mode this only shapes bar 1 — the rest chain from it.
            </p>
          )}
        </div>
        )}

        {/* Inversions */}
        {!pocketMode && (
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Inversions
          </label>
          <div className="flex flex-wrap gap-1.5">
            {INVERSION_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onInversionModeChange(m.id)}
                aria-pressed={inversionModeId === m.id}
                title={INVERSION_HINTS[m.id]}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-sm transition-colors",
                  inversionModeId === m.id
                    ? "border-primary bg-primary/10 font-medium"
                    : "border-border bg-background hover:border-primary/60 hover:bg-accent"
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {INVERSION_HINTS[inversionModeId] || INVERSION_HINTS.roots}
          </p>
        </div>
        )}

        {/* Range */}
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Range · {spanLabel(low, high)}
          </label>
          <div className="flex items-center gap-2">
            <Select value={String(low)} onValueChange={(v) => onRangeChange(Number(v), high)}>
              <SelectTrigger aria-label="Lowest note">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rangeOptions.map((o) => (
                  <SelectItem key={o.midi} value={String(o.midi)}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">to</span>
            <Select value={String(high)} onValueChange={(v) => onRangeChange(low, Number(v))}>
              <SelectTrigger aria-label="Highest note">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rangeOptions.map((o) => (
                  <SelectItem key={o.midi} value={String(o.midi)}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Chords */}
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Chords
          </label>
          <div className="flex flex-wrap items-center gap-1.5">
            {DIATONIC_CHORDS.map((c) => (
              <button
                key={c.degree}
                type="button"
                onClick={() => addChord(c.degree)}
                disabled={atMax}
                className="flex flex-col items-center rounded-lg border border-border bg-background px-3 py-1.5 text-sm transition-colors hover:border-primary/60 hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="font-medium">{c.roman}</span>
                <span className="text-xs text-muted-foreground">
                  {chordLabel(keyName, c.degree)}
                </span>
              </button>
            ))}
            {atMax && (
              <span className="text-xs text-muted-foreground">{MAX_BARS} bars max</span>
            )}
          </div>

          <div
            className={cn(
              "mt-2 flex flex-wrap items-center gap-1.5",
              typedActive && !progressionError && "opacity-60"
            )}
          >
            {progression.length === 0 && (
              <span className="text-sm text-muted-foreground">
                No chords yet — click one above.
              </span>
            )}
            {progression.map((degree, i) => (
              <span
                key={`${i}-${degree}`}
                className="flex items-center gap-1 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-medium"
              >
                {i + 1}. {chordLabel(keyName, degree)}
                <button
                  type="button"
                  aria-label={`Remove ${i + 1}. ${chordLabel(keyName, degree)}`}
                  onClick={() => removeAt(i)}
                  className="ml-0.5 text-muted-foreground hover:text-foreground"
                >
                  ×
                </button>
              </span>
            ))}
            {progression.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setText("");
                  onProgressionChange([]);
                }}
              >
                Clear
              </Button>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {PROGRESSION_PRESETS.map((p) => (
              <Button
                key={p.id}
                variant="outline"
                size="sm"
                onClick={() => {
                  // A preset fills both paths so chips and text agree.
                  onProgressionChange([...p.degrees]);
                  setText(p.degrees.join(" "));
                }}
              >
                {p.label}
              </Button>
            ))}
          </div>

          <div className="mt-2 space-y-1.5">
            <input
              type="text"
              value={progressionText || ""}
              onChange={(e) => setText(e.target.value)}
              placeholder="or type it — like 1 5 6 4"
              aria-label="Custom progression"
              spellCheck={false}
              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 font-mono text-sm focus:border-primary focus:outline-none"
            />
            {progressionError ? (
              <p className="text-xs text-amber-700">{progressionError}</p>
            ) : typedActive ? (
              <p className="text-xs text-muted-foreground">
                Typed progression is in charge — clear the box (or click a chord chip) to
                go back to the chips.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Or type scale degrees 1–7, split by spaces, commas, or dashes — up to{" "}
                {MAX_PROGRESSION_CHORDS} chords, past the {MAX_BARS}-bar chip limit.
              </p>
            )}
          </div>
        </div>

        {/* Tempo */}
        {!pocketMode && (
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Tempo · {bpm} BPM
          </label>
          <input
            type="range"
            min={40}
            max={180}
            value={bpm}
            aria-label="Tempo"
            onChange={(e) => onBpmChange(Number(e.target.value))}
            className="w-full"
          />
        </div>
        )}

        {/* Mode + transport */}
        {!pocketMode && (
        <div className="sm:col-span-2 lg:col-span-2">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Mode
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex overflow-hidden rounded-lg border border-border">
              {MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onModeChange(m)}
                  aria-pressed={mode === m}
                  className={cn(
                    "px-3 py-1.5 text-sm capitalize transition-colors",
                    mode === m
                      ? "bg-primary text-primary-foreground"
                      : "bg-background hover:bg-accent"
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-sm">
              <Checkbox checked={metronome} onCheckedChange={(v) => onMetronomeChange(!!v)} />
              Metronome
            </label>
            <Button
              onClick={onTogglePlay}
              size="lg"
              className="ml-auto"
              variant={playing ? "destructive" : "default"}
            >
              {playing ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {playing ? "Stop" : "Play"}
            </Button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{MODE_BLURB[mode]}</p>
        </div>
        )}

        {/* MIDI badge */}
        <div className="flex items-end">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
              midiReady
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "border-border bg-background text-muted-foreground"
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                midiReady ? "bg-emerald-500" : "bg-muted-foreground/50"
              )}
            />
            {midiReady ? `MIDI: ${midiInputs[0]}` : "MIDI: none — use on-screen keys or A–K row"}
          </span>
        </div>
      </CardContent>

      {drillError && (
        <div className="mx-6 mb-6 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {drillError}
        </div>
      )}
    </Card>
  );
}
